import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { useSettings } from '@/contexts/SettingsContext';
import { useTranslations } from '@/hooks/useTranslations';
import { getGanttInstanceFactory, type DhtmlxGanttInstance } from '@/lib/projects/dhtmlxGanttFactory';
import { buildGanttTimelineCellClasses, isDateInSameLocalWeekAs, isSameCalendarDay } from '@/lib/projects/ganttCalendar';
import { buildPortfolioGanttTimelineCss } from '@/lib/projects/ganttSharedThemeCss';
import {
    buildGanttTaskStatusCssRules,
    buildPortfolioStaticBarStatusCssRules,
    ganttStatusClassSlug,
    hslTripletToCss,
    projectTaskColorToHslTriplet,
    resolveTaskStatusSwatchFromSettings,
} from '@/lib/projects/ganttTaskStatusStyles';
import { cn } from '@/lib/utils';
import type { ProjectTask } from '@/types';
import { Link, usePage } from '@inertiajs/react';
import 'dhtmlx-gantt/codebase/dhtmlxgantt.css';
import { useLayoutEffect, useMemo, useRef, type CSSProperties } from 'react';
import { route } from 'ziggy-js';

const DAY_IN_MS = 86_400_000;
const DAY_COLUMN_WIDTH = 40;
const TASK_COLUMN_WIDTH = 292;
const GRID_START_DATE_COLUMN_WIDTH = 108;
const GRID_END_DATE_COLUMN_WIDTH = 112;
const GRID_TOTAL_WIDTH = TASK_COLUMN_WIDTH + GRID_START_DATE_COLUMN_WIDTH + GRID_END_DATE_COLUMN_WIDTH;
const PORTFOLIO_ROW_HEIGHT = 52;
const PORTFOLIO_BAR_HEIGHT = 32;
const PORTFOLIO_SCALE_HEIGHT = 52;
const PORTFOLIO_VERTICAL_FRAME_PADDING = 2;

interface PortfolioGanttCardTaskRow {
    id: string;
    text: string;
    start_date: string;
    duration: number;
    parent?: string;
    type: 'project' | 'task';
    status?: string;
    assigned_to?: string | null;
    open?: boolean;
}

interface PortfolioGanttCardLinkRow {
    id: string;
    source: string;
    target: string;
    type: '0';
}

export interface PortfolioGanttProjectPayload {
    id: string;
    contract_name: string;
    contract_number: string;
    status?: string;
    start_date: string | null;
    end_date: string | null;
    tasks: ProjectTask[];
}

interface PortfolioProjectGanttCardProps {
    project: PortfolioGanttProjectPayload;
    timelineYear: number;
    className?: string;
}

function toYmd(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

function startOfDay(date: Date): Date {
    const copy = new Date(date);
    copy.setHours(0, 0, 0, 0);

    return copy;
}

function parseIsoDate(value: string | null | undefined): Date | null {
    if (!value || value.length < 10) {
        return null;
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return null;
    }

    return startOfDay(parsed);
}

function escapeHtmlLabel(value: string): string {
    return value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;');
}

function formatGridDateYmd(ymd: string): string {
    return /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : '';
}

function coerceTaskDateToYmd(value: unknown): string {
    if (value == null) {
        return '';
    }
    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) {
            return '';
        }
        return toYmd(value);
    }
    const s = String(value).trim();
    const iso = /^(\d{4}-\d{2}-\d{2})/.exec(s);
    if (iso) {
        return iso[1];
    }
    const parsed = new Date(s);
    if (Number.isNaN(parsed.getTime())) {
        return '';
    }
    return toYmd(parsed);
}

function resolveGridEndYmd(task: { start_date?: string; end_date?: string; duration?: number }): string {
    const fromEnd = coerceTaskDateToYmd(task.end_date);
    if (fromEnd) {
        return fromEnd;
    }

    const startYmd = coerceTaskDateToYmd(task.start_date);
    if (!startYmd || task.duration == null || Number.isNaN(Number(task.duration))) {
        return '';
    }

    const dur = Math.max(0, Math.floor(Number(task.duration)));
    const start = parseIsoDate(startYmd);
    if (!start) {
        return '';
    }

    if (dur <= 0) {
        return toYmd(start);
    }

    const end = new Date(start);
    end.setDate(end.getDate() + dur - 1);
    return toYmd(end);
}

function ensureDurationDays(start: Date, end: Date | null, fallback = 1): number {
    if (end === null) {
        return Math.max(1, fallback);
    }

    const diff = Math.ceil((end.getTime() - start.getTime()) / DAY_IN_MS);

    return Math.max(1, diff || fallback);
}

function sortTasksForTree(tasks: ProjectTask[]): ProjectTask[] {
    return [...tasks].sort((left, right) => {
        const sortDiff = (left.sort_order ?? 0) - (right.sort_order ?? 0);
        if (sortDiff !== 0) {
            return sortDiff;
        }

        const leftDate = parseIsoDate(left.start_date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        const rightDate = parseIsoDate(right.start_date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        if (leftDate !== rightDate) {
            return leftDate - rightDate;
        }

        return left.name.localeCompare(right.name, 'en', { sensitivity: 'base' });
    });
}

function resolveProjectHeading(project: PortfolioGanttProjectPayload): string {
    const name = project.contract_name?.trim() ?? '';
    const number = project.contract_number?.trim() ?? '';

    if (number && name) {
        return `${number} ${name}`;
    }

    return number || name || project.id;
}

function humanizeStatus(value: string): string {
    return value
        .replaceAll('_', ' ')
        .trim()
        .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function buildProjectGanttData(
    project: PortfolioGanttProjectPayload,
    timelineYear: number,
): {
    rows: PortfolioGanttCardTaskRow[];
    links: PortfolioGanttCardLinkRow[];
    minDate: Date;
    maxDate: Date;
} {
    const fallbackStart = startOfDay(new Date(timelineYear, 0, 1));
    const taskStarts = project.tasks.map((task) => parseIsoDate(task.start_date)).filter((value): value is Date => value !== null);
    const taskEnds = project.tasks.map((task) => parseIsoDate(task.end_date)).filter((value): value is Date => value !== null);

    const projectStart = parseIsoDate(project.start_date) ?? taskStarts.sort((a, b) => a.getTime() - b.getTime())[0] ?? fallbackStart;
    const projectEnd =
        parseIsoDate(project.end_date) ??
        taskEnds.sort((a, b) => b.getTime() - a.getTime())[0] ??
        startOfDay(new Date(projectStart.getTime() + 30 * DAY_IN_MS));

    const projectRowId = `project-${project.id}`;
    const rows: PortfolioGanttCardTaskRow[] = [
        {
            id: projectRowId,
            text: resolveProjectHeading(project),
            start_date: toYmd(projectStart),
            duration: ensureDurationDays(projectStart, projectEnd, 30),
            type: 'project',
            status: 'active',
            assigned_to: null,
            open: true,
        },
    ];
    const links: PortfolioGanttCardLinkRow[] = [];

    const sortedTasks = sortTasksForTree(project.tasks);
    const knownTaskIds = new Set(sortedTasks.map((task) => task.id));

    let minDate = new Date(projectStart);
    let maxDate = new Date(projectEnd);

    for (const task of sortedTasks) {
        const rowId = `task-${task.id}`;
        const start = parseIsoDate(task.start_date) ?? projectStart;
        const end = parseIsoDate(task.end_date);
        const duration = ensureDurationDays(start, end, Number(task.duration_days) || 1);
        const parent = task.parent_task_id && knownTaskIds.has(task.parent_task_id) ? `task-${task.parent_task_id}` : projectRowId;

        rows.push({
            id: rowId,
            text: task.name,
            start_date: toYmd(start),
            duration,
            parent,
            type: 'task',
            status: task.status,
            assigned_to: resolveTaskAssigneeLabel(task),
        });

        const taskEnd = new Date(start);
        taskEnd.setDate(taskEnd.getDate() + duration);
        if (start.getTime() < minDate.getTime()) {
            minDate = start;
        }
        if (taskEnd.getTime() > maxDate.getTime()) {
            maxDate = taskEnd;
        }

        if (Array.isArray(task.dependencies)) {
            for (const dependencyId of task.dependencies) {
                const sourceId = String(dependencyId).trim();
                if (!sourceId || !knownTaskIds.has(sourceId)) {
                    continue;
                }
                links.push({
                    id: `${sourceId}-${task.id}`,
                    source: `task-${sourceId}`,
                    target: rowId,
                    type: '0',
                });
            }
        }
    }

    return { rows, links, minDate, maxDate };
}

interface TimelineDayCell {
    date: Date;
    key: string;
    isWeekend: boolean;
    isToday: boolean;
    isCurrentWeek: boolean;
}

interface TimelineMonthSegment {
    key: string;
    label: string;
    span: number;
}

interface StaticRowModel {
    id: string;
    label: string;
    depth: number;
    isProject: boolean;
    status: string | undefined;
    assignedTo: string | null;
    startIndex: number;
    spanDays: number;
}

function resolveTaskAssigneeLabel(task: ProjectTask): string | null {
    if (!task.assignee) {
        return null;
    }

    const fullName = `${task.assignee.first_name ?? ''} ${task.assignee.last_name ?? ''}`.trim();

    return fullName || task.assignee.email || null;
}

function createTimelineDays(rangeStart: Date, rangeEnd: Date): TimelineDayCell[] {
    const days: TimelineDayCell[] = [];
    const cursor = startOfDay(rangeStart);
    const end = startOfDay(rangeEnd);
    const today = new Date();

    while (cursor.getTime() <= end.getTime()) {
        const day = new Date(cursor);
        days.push({
            date: day,
            key: toYmd(day),
            isWeekend: day.getDay() === 0 || day.getDay() === 6,
            isToday: isSameCalendarDay(day, today),
            isCurrentWeek: isDateInSameLocalWeekAs(today, day),
        });
        cursor.setDate(cursor.getDate() + 1);
    }

    return days;
}

function createTimelineMonthSegments(days: TimelineDayCell[]): TimelineMonthSegment[] {
    const segments: TimelineMonthSegment[] = [];

    for (const day of days) {
        const key = `${day.date.getFullYear()}-${String(day.date.getMonth() + 1).padStart(2, '0')}`;
        const tail = segments[segments.length - 1];
        if (tail && tail.key === key) {
            tail.span += 1;
            continue;
        }

        segments.push({
            key,
            label: key,
            span: 1,
        });
    }

    return segments;
}

function createStaticRows(rows: PortfolioGanttCardTaskRow[], rangeStart: Date, dayCount: number): StaticRowModel[] {
    const rowById = new Map(rows.map((row) => [row.id, row]));
    const depthMemo = new Map<string, number>();

    const resolveDepth = (rowId: string): number => {
        if (depthMemo.has(rowId)) {
            return depthMemo.get(rowId) ?? 0;
        }

        const row = rowById.get(rowId);
        if (!row?.parent || !rowById.has(row.parent)) {
            depthMemo.set(rowId, 0);
            return 0;
        }

        const depth = Math.min(8, resolveDepth(row.parent) + 1);
        depthMemo.set(rowId, depth);
        return depth;
    };

    return rows.map((row) => {
        const start = parseIsoDate(row.start_date) ?? rangeStart;
        const startIndexRaw = Math.floor((start.getTime() - rangeStart.getTime()) / DAY_IN_MS);
        const startIndex = Math.max(0, Math.min(dayCount - 1, Number.isFinite(startIndexRaw) ? startIndexRaw : 0));
        const spanDays = Math.max(1, row.duration);

        return {
            id: row.id,
            label: row.text,
            depth: resolveDepth(row.id),
            isProject: row.type === 'project',
            status: row.status,
            assignedTo: row.assigned_to ?? null,
            startIndex,
            spanDays: Math.min(dayCount - startIndex, spanDays),
        };
    });
}

const PROJECT_GANTT_CARD_STYLE = `
  .portfolio-gantt-chart-viewport {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: var(--portfolio-gantt-chart-height);
    overflow: hidden;
  }

  .portfolio-gantt-card-chart {
    --portfolio-gantt-weekend-bg: hsl(340 42% 95%);
    --portfolio-gantt-header-bg: color-mix(in oklch, var(--muted) 32%, transparent);
    --portfolio-gantt-day-grid-line: hsl(215 16% 76%);
    width: 100%;
    flex: 1 1 0%;
    min-height: 0;
    height: 100%;
  }

  .dark .portfolio-gantt-card-chart {
    --portfolio-gantt-weekend-bg: hsl(340 18% 22%);
    --portfolio-gantt-header-bg: color-mix(in oklch, var(--muted) 35%, transparent);
    --portfolio-gantt-day-grid-line: hsl(215 12% 34%);
  }

  .portfolio-gantt-card-chart .gantt_container {
    border: none !important;
    font-family: var(--font-sans) !important;
    background: var(--card) !important;
    color: var(--card-foreground) !important;
  }

  .portfolio-gantt-card-chart .gantt_grid_scale,
  .portfolio-gantt-card-chart .gantt_task_scale {
    background: var(--portfolio-gantt-header-bg) !important;
    border-color: var(--border) !important;
    border-right: 1px solid color-mix(in oklch, var(--border) 45%, transparent) !important;
    border-bottom: 1px solid color-mix(in oklch, var(--border) 35%, transparent) !important;
  }

  .portfolio-gantt-card-chart .gantt_grid_head_cell,
  .portfolio-gantt-card-chart .gantt_scale_cell {
    background: var(--portfolio-gantt-header-bg) !important;
    border-right: 1px solid color-mix(in oklch, var(--border) 45%, transparent) !important;
    border-bottom: 1px solid color-mix(in oklch, var(--border) 35%, transparent) !important;
    color: var(--muted-foreground) !important;
    font-size: 12px !important;
    font-weight: 500 !important;
    letter-spacing: 0 !important;
    text-transform: none !important;
  }

  .portfolio-gantt-card-chart .gantt_scale_line:first-child .gantt_scale_cell {
    height: 40px !important;
    line-height: 40px !important;
    font-size: 13px !important;
    font-weight: 600 !important;
    justify-content: flex-start !important;
    text-align: left !important;
    padding-left: 10px !important;
  }

  .portfolio-gantt-card-chart .gantt_scale_line:last-child .gantt_scale_cell {
    height: 30px !important;
    line-height: 30px !important;
    text-align: center !important;
  }

  .portfolio-gantt-card-chart .gantt_row,
  .portfolio-gantt-card-chart .gantt_task_row {
    height: ${PORTFOLIO_ROW_HEIGHT}px !important;
    min-height: ${PORTFOLIO_ROW_HEIGHT}px !important;
    border-bottom: 1px solid var(--portfolio-gantt-day-grid-line) !important;
    background: var(--card) !important;
  }

  .portfolio-gantt-card-chart .gantt_row:hover,
  .portfolio-gantt-card-chart .gantt_task_row:hover {
    background: color-mix(in oklch, var(--muted) 20%, transparent) !important;
  }

  .portfolio-gantt-card-chart .gantt_cell {
    height: ${PORTFOLIO_ROW_HEIGHT}px !important;
    border-right: 1px solid var(--portfolio-gantt-day-grid-line) !important;
    color: var(--foreground) !important;
    font-size: 12px !important;
    padding: 0 8px !important;
  }

  .portfolio-gantt-card-chart .gantt_tree_content {
    display: flex !important;
    align-items: center !important;
    height: ${PORTFOLIO_ROW_HEIGHT}px !important;
    min-height: ${PORTFOLIO_ROW_HEIGHT}px !important;
  }

  .portfolio-gantt-card-chart .gantt_task_bg,
  .portfolio-gantt-card-chart .gantt_task_area,
  .portfolio-gantt-card-chart .gantt_task_cell {
    background: var(--card) !important;
  }

  .portfolio-gantt-card-chart .gantt_task_scale .gantt_scale_cell,
  .portfolio-gantt-card-chart .gantt_task_bg .gantt_task_cell,
  .portfolio-gantt-card-chart .gantt_task_row,
  .portfolio-gantt-card-chart .gantt_ver_line,
  .portfolio-gantt-card-chart .gantt_task_cell {
    border-right: none !important;
    border-bottom: none !important;
  }

  .portfolio-gantt-card-chart .gantt_task_scale .gantt_scale_cell {
    border-bottom: 1px solid color-mix(in oklch, var(--border) 35%, transparent) !important;
  }

  .portfolio-gantt-card-chart .gantt_scale_cell.weekend,
  .portfolio-gantt-card-chart .gantt_task_cell.weekend {
    background: var(--portfolio-gantt-weekend-bg) !important;
  }

  .portfolio-gantt-card-chart .gantt_task_line {
    --portfolio-gantt-task-status: 215 20% 65%;
    border: 1px dashed hsl(var(--portfolio-gantt-task-status) / 0.68) !important;
    border-radius: 4px !important;
    background: color-mix(in oklch, var(--card) 94%, transparent) !important;
    box-shadow:
      0 0 0 1px hsl(var(--portfolio-gantt-task-status) / 0.62),
      0 2px 0 hsl(var(--portfolio-gantt-task-status) / 0.12),
      0 6px 18px -3px hsl(var(--portfolio-gantt-task-status) / 0.44) !important;
    height: ${PORTFOLIO_BAR_HEIGHT}px !important;
    margin-top: 10px !important;
    position: relative !important;
    transition: border-color 0.16s ease, box-shadow 0.16s ease !important;
  }

  .portfolio-gantt-card-chart .gantt_task_line::before {
    content: '';
    position: absolute;
    left: 7px;
    top: 50%;
    width: 6px;
    height: 6px;
    border-radius: 9999px;
    transform: translateY(-50%);
    background: hsl(var(--portfolio-gantt-task-status)) !important;
    box-shadow: 0 0 0 1px hsl(var(--portfolio-gantt-task-status) / 0.2) !important;
    pointer-events: none;
  }

  .portfolio-gantt-card-chart .gantt_task_line:hover {
    border-color: hsl(var(--portfolio-gantt-task-status) / 0.72) !important;
    box-shadow:
      0 0 0 1px hsl(var(--portfolio-gantt-task-status) / 0.72),
      0 2px 0 hsl(var(--portfolio-gantt-task-status) / 0.16),
      0 8px 22px -2px hsl(var(--portfolio-gantt-task-status) / 0.52) !important;
  }

  .portfolio-gantt-card-chart .gantt_task_content {
    font-size: 12px !important;
    font-weight: 500 !important;
    color: var(--foreground) !important;
    line-height: 1.2 !important;
    padding: 0 8px 0 18px !important;
    text-align: left !important;
    text-shadow: none !important;
    display: flex !important;
    align-items: center !important;
  }

  .portfolio-gantt-card-chart .gantt_task_line.gantt_project {
    --portfolio-gantt-task-status: 219 81% 58%;
    border-style: dashed !important;
  }

  .portfolio-gantt-card-chart .gantt_task_progress {
    background: transparent !important;
  }

  .portfolio-gantt-card-chart .gantt_cell .task-status-bubble {
    display: flex !important;
    align-items: center !important;
    gap: 6px !important;
    width: 100% !important;
    height: 100% !important;
    padding: 0 !important;
    margin-left: 4px !important;
    background: transparent !important;
  }

  .portfolio-gantt-card-chart .gantt_cell .status-indicator {
    width: 8px !important;
    height: 8px !important;
    border-radius: 50% !important;
    flex-shrink: 0 !important;
    box-shadow: 0 0 0 2px hsla(0, 0%, 0%, 0.1) !important;
    transition: transform 0.2s ease !important;
    margin-top: 0 !important;
    background: var(--status-color) !important;
  }

  .portfolio-gantt-card-chart .gantt_row:hover .status-indicator {
    transform: scale(1.2) !important;
    box-shadow: 0 0 0 3px hsla(0, 0%, 0%, 0.15) !important;
  }

  .portfolio-gantt-card-chart .gantt_cell .task-content {
    flex: 1 !important;
    min-width: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: center !important;
  }

  .portfolio-gantt-card-chart .gantt_cell .task-name {
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    font-weight: 500 !important;
    line-height: 1.2 !important;
  }

  .portfolio-gantt-card-chart .gantt_cell .text-muted {
    font-size: 10px !important;
    color: var(--muted-foreground) !important;
    line-height: 1.2 !important;
    opacity: 0.8 !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    display: none !important;
  }

  .portfolio-gantt-card-chart .gantt_grid_head_add,
  .portfolio-gantt-card-chart .gantt_add {
    display: none !important;
  }

  .portfolio-gantt-card-chart .gantt_grid_head_cell:nth-child(2),
  .portfolio-gantt-card-chart .gantt_grid_head_cell:nth-child(3) {
    justify-content: center !important;
    text-align: center !important;
    padding-left: 4px !important;
    padding-right: 4px !important;
  }

  .portfolio-gantt-card-chart .gantt_grid .gantt_cell:nth-child(2),
  .portfolio-gantt-card-chart .gantt_grid .gantt_cell:nth-child(3) {
    padding-left: 4px !important;
    padding-right: 4px !important;
  }

  .portfolio-gantt-card-chart .gantt-grid-date-cell {
    display: inline-block;
    width: 100%;
    text-align: center;
    font-size: 11px;
    line-height: 1.15;
    letter-spacing: -0.02em;
    color: var(--muted-foreground);
    font-variant-numeric: tabular-nums;
  }

  .portfolio-gantt-card-chart .gantt_task_content .gantt-task-inline {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
  }

  .portfolio-gantt-card-chart .gantt_task_content .gantt-task-title,
  .portfolio-gantt-card-chart .gantt_task_content .gantt-task-assignee {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  .portfolio-gantt-card-chart .gantt_task_content .gantt-task-assignee {
    font-weight: 400;
    color: var(--muted-foreground);
  }

  .portfolio-gantt-card-chart .gantt_task_content .gantt-task-sep {
    color: var(--muted-foreground);
    opacity: 0.65;
    font-weight: 400;
    flex: 0 0 auto;
  }

  .portfolio-gantt-card-chart .gantt_marker_area .gantt_marker.gantt_marker_portfolio_today {
    width: 1px !important;
    min-width: 1px;
    background-color: #dc2626 !important;
    background: #dc2626 !important;
    opacity: 1;
    pointer-events: none;
    z-index: 35;
  }

  .dark .portfolio-gantt-card-chart .gantt_marker_area .gantt_marker.gantt_marker_portfolio_today {
    background-color: #f87171 !important;
    background: #f87171 !important;
  }

  .portfolio-gantt-static-shell {
    width: 100%;
    flex: 1 1 0%;
    min-height: 0;
    height: 100%;
    overflow-x: auto;
    overflow-y: hidden;
  }

  .portfolio-gantt-static {
    position: relative;
    min-width: fit-content;
    border-top: 1px solid color-mix(in oklch, var(--border) 75%, transparent);
    border-bottom: 1px solid color-mix(in oklch, var(--border) 75%, transparent);
    background: var(--card);
    --gantt-current-week-bg: color-mix(in oklch, var(--muted) 32%, transparent);
  }

  .dark .portfolio-gantt-static {
    --gantt-current-week-bg: color-mix(in oklch, var(--muted) 24%, transparent);
  }

  .portfolio-gantt-static-today-line {
    position: absolute;
    top: 48px;
    bottom: 0;
    width: 1px;
    margin-left: -0.5px;
    background: #dc2626;
    z-index: 4;
    pointer-events: none;
  }

  .dark .portfolio-gantt-static-today-line {
    background: #f87171;
  }

  .portfolio-gantt-static-row {
    display: grid;
    grid-template-columns: ${TASK_COLUMN_WIDTH}px auto;
    min-width: fit-content;
  }

  .portfolio-gantt-static-left-head,
  .portfolio-gantt-static-month-cell,
  .portfolio-gantt-static-day-cell {
    background: var(--portfolio-gantt-header-bg);
    color: var(--muted-foreground);
    border-right: none;
    border-bottom: 1px solid color-mix(in oklch, var(--border) 35%, transparent);
    font-size: 12px;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 24px;
  }

  .portfolio-gantt-static-left-head {
    justify-content: flex-start;
    padding: 0 8px;
    border-right: 1px solid color-mix(in oklch, var(--border) 45%, transparent);
  }

  .portfolio-gantt-static-left-cell {
    border-right: 1px solid color-mix(in oklch, var(--border) 45%, transparent);
    border-bottom: 1px solid var(--portfolio-gantt-day-grid-line);
    color: var(--foreground);
    font-size: 12px;
    height: ${PORTFOLIO_ROW_HEIGHT}px;
    display: flex;
    align-items: center;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    padding-right: 10px;
  }

  .portfolio-gantt-static-left-cell .task-status-bubble {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    height: 100%;
    margin-left: 0;
  }

  .portfolio-gantt-static-left-cell .status-indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    box-shadow: 0 0 0 2px hsla(0, 0%, 0%, 0.1);
    margin-top: 0;
    background: var(--status-color);
  }

  .portfolio-gantt-static-left-cell .task-content {
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .portfolio-gantt-static-left-cell .task-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.2;
    font-weight: 500;
  }

  .portfolio-gantt-static-left-cell .text-muted {
    font-size: 11px;
    color: var(--muted-foreground);
    line-height: 1.2;
    opacity: 0.8;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: none;
  }

  .portfolio-gantt-static-right-grid {
    position: relative;
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: ${DAY_COLUMN_WIDTH}px;
    min-height: ${PORTFOLIO_ROW_HEIGHT}px;
    border-bottom: 1px solid color-mix(in oklch, var(--border) 35%, transparent);
    background: var(--card);
  }

  .portfolio-gantt-static-right-cell {
    border-right: none;
    min-height: ${PORTFOLIO_ROW_HEIGHT}px;
    background: var(--card);
  }

  .portfolio-gantt-static-right-cell.weekend,
  .portfolio-gantt-static-day-cell.weekend,
  .portfolio-gantt-static-month-cell.weekend {
    background: var(--portfolio-gantt-weekend-bg);
  }

  .portfolio-gantt-static-day-cell.gantt_current_week:not(.weekend),
  .portfolio-gantt-static-right-cell.gantt_current_week:not(.weekend) {
    background: var(--gantt-current-week-bg);
  }

  .portfolio-gantt-static-day-cell.gantt_current_week.weekend,
  .portfolio-gantt-static-right-cell.gantt_current_week.weekend {
    background: color-mix(in oklch, var(--muted) 26%, transparent);
  }

  .portfolio-gantt-static-day-cell.today {
    background: transparent;
    color: var(--foreground);
    font-weight: 600;
    box-shadow: inset 0 0 0 2px #dc2626;
    border-radius: 9999px;
  }

  .dark .portfolio-gantt-static-day-cell.today {
    box-shadow: inset 0 0 0 2px #f87171;
  }

  .portfolio-gantt-static-right-cell.today {
    background: transparent;
  }

  .portfolio-gantt-static-bar {
    --portfolio-gantt-task-status: 215 20% 65%;
    position: absolute;
    top: 8px;
    height: ${PORTFOLIO_BAR_HEIGHT}px;
    border-radius: 8px;
    border: 1px solid hsl(var(--portfolio-gantt-task-status) / 0.28);
    background: color-mix(in oklch, var(--card) 98%, transparent);
    box-shadow:
      0 0 0 1px hsl(var(--portfolio-gantt-task-status) / 0.12),
      0 0 20px -4px hsl(var(--portfolio-gantt-task-status) / 0.35);
    color: var(--foreground);
    font-size: 12px;
    font-weight: 500;
    line-height: ${PORTFOLIO_BAR_HEIGHT}px;
    padding: 0 8px 0 22px;
    text-align: left;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    pointer-events: none;
    z-index: 2;
  }

  .portfolio-gantt-static-bar.gantt_project {
    --portfolio-gantt-task-status: 219 81% 58%;
    border-style: dashed;
  }

  .portfolio-gantt-static-bar::before {
    content: '';
    position: absolute;
    left: 8px;
    top: 50%;
    width: 8px;
    height: 8px;
    border-radius: 9999px;
    transform: translateY(-50%);
    background: hsl(var(--portfolio-gantt-task-status));
    box-shadow: 0 0 0 2px hsl(var(--portfolio-gantt-task-status) / 0.16);
  }

`;

const PORTFOLIO_TODAY_MARKER_ID = 'portfolio-gantt-today-marker';

export default function PortfolioProjectGanttCard({ project, timelineYear, className }: PortfolioProjectGanttCardProps) {
    const { t } = useTranslations();
    const { settings } = useSettings();
    const projectTaskStatuses = useMemo(() => settings.projectTaskStatuses ?? [], [settings.projectTaskStatuses]);
    const page = usePage<{ locale?: string }>();
    const locale = page.props.locale ?? 'en';
    const containerRef = useRef<HTMLDivElement>(null);
    const heading = useMemo(() => resolveProjectHeading(project), [project]);
    const ganttFactory = useMemo(() => getGanttInstanceFactory(), []);
    const projectShowHref = useMemo(() => route('projects.show', project.id), [project.id]);
    const projectStatusMeta = useMemo(() => {
        const normalizedStatus = String(project.status ?? '').trim();
        const matched = settings.projectStatuses.find((status) => status.value === normalizedStatus);
        const label = matched?.label || (normalizedStatus ? humanizeStatus(normalizedStatus) : t('projects.tables.status', 'Status'));
        const color = hslTripletToCss(projectTaskColorToHslTriplet(matched?.color ?? 'bg-gray-100'));
        return { label, color };
    }, [project.status, settings.projectStatuses, t]);
    const taskColumnLabel = t('projects.index.portfolio_gantt.column_task', 'Project / Task');
    const unassignedTaskLabel = t('projects.gantt.unassigned', 'Unassigned');

    const portfolioThemeCss = useMemo(() => {
        const timeline = buildPortfolioGanttTimelineCss({
            rowHeight: PORTFOLIO_ROW_HEIGHT,
            barHeight: PORTFOLIO_BAR_HEIGHT,
        });
        const dhtmlxStatus = buildGanttTaskStatusCssRules(
            ['.portfolio-gantt-card-chart .gantt_container'],
            projectTaskStatuses,
            '--portfolio-gantt-task-status',
        );
        const staticBars = buildPortfolioStaticBarStatusCssRules('.portfolio-gantt-static', projectTaskStatuses);
        return `${timeline}\n${dhtmlxStatus}\n${staticBars}`;
    }, [projectTaskStatuses]);

    const chartModel = useMemo(() => {
        const { rows, links, minDate, maxDate } = buildProjectGanttData(project, timelineYear);

        const rangeStart = startOfDay(new Date(minDate));
        rangeStart.setDate(rangeStart.getDate() - 10);
        const rangeEnd = startOfDay(new Date(maxDate));
        rangeEnd.setDate(rangeEnd.getDate() + 28);
        rangeEnd.setHours(23, 59, 59, 999);

        const days = createTimelineDays(rangeStart, rangeEnd);
        const monthSegments = createTimelineMonthSegments(days);
        const staticRows = createStaticRows(rows, rangeStart, days.length);

        return {
            rows,
            links,
            rangeStart,
            rangeEnd,
            days,
            monthSegments,
            staticRows,
        };
    }, [project, timelineYear]);

    const staticTodayLineLeftPx = useMemo(() => {
        const idx = chartModel.days.findIndex((d) => d.isToday);
        if (idx < 0) {
            return null;
        }
        return TASK_COLUMN_WIDTH + idx * DAY_COLUMN_WIDTH + DAY_COLUMN_WIDTH / 2;
    }, [chartModel.days]);
    const chartHeightStyle = useMemo<CSSProperties>(() => {
        const rowCount = Math.max(1, chartModel.rows.length);
        const chartHeightPx = PORTFOLIO_SCALE_HEIGHT + rowCount * PORTFOLIO_ROW_HEIGHT + PORTFOLIO_VERTICAL_FRAME_PADDING;

        return {
            ['--portfolio-gantt-chart-height' as string]: `${chartHeightPx}px`,
        };
    }, [chartModel.rows.length]);

    // `t` from useTranslations changes identity every render; `locale` is the stable signal to refresh column labels.
    useLayoutEffect(() => {
        if (!ganttFactory) {
            return;
        }

        const container = containerRef.current;
        if (!container) {
            return;
        }

        const instance = ganttFactory() as DhtmlxGanttInstance;
        const { rows, links, rangeStart, rangeEnd } = chartModel;

        instance.config.readonly = true;
        instance.config.drag_links = false;
        instance.config.drag_move = false;
        instance.config.drag_resize = false;
        instance.config.drag_progress = false;
        instance.config.show_links = false;
        instance.config.open_tree_initially = true;
        instance.config.row_height = PORTFOLIO_ROW_HEIGHT;
        instance.config.bar_height = PORTFOLIO_BAR_HEIGHT;
        instance.config.grid_width = GRID_TOTAL_WIDTH;
        instance.config.column_width = DAY_COLUMN_WIDTH;
        instance.config.date_format = '%Y-%m-%d';
        instance.config.start_date = rangeStart;
        instance.config.end_date = rangeEnd;
        instance.config.columns = [
            {
                name: 'text',
                label: taskColumnLabel,
                tree: true,
                width: TASK_COLUMN_WIDTH,
                resize: false,
                template(task: { text?: string; status?: string; assigned_to?: string | null; type?: 'project' | 'task' }): string {
                    const statusColor = resolveTaskStatusSwatchFromSettings({ status: task.status, type: task.type }, projectTaskStatuses);
                    const safeTaskText = escapeHtmlLabel(String(task.text ?? ''));
                    const secondaryText = task.type === 'project' ? '' : escapeHtmlLabel(task.assigned_to || unassignedTaskLabel);

                    return `<div class="task-status-bubble" style="--status-color: ${statusColor};">
                        <div class="status-indicator" style="background: ${statusColor} !important;"></div>
                        <div class="task-content">
                            <div class="task-name">${safeTaskText}</div>
                            ${secondaryText !== '' ? `<div class="text-muted">${secondaryText}</div>` : ''}
                        </div>
                    </div>`;
                },
            },
            {
                name: 'start_date',
                label: t('projects.gantt.grid.start_date', 'Start'),
                align: 'center',
                width: GRID_START_DATE_COLUMN_WIDTH,
                resize: false,
                template(task: { start_date?: string }): string {
                    const startYmd = formatGridDateYmd(coerceTaskDateToYmd(task.start_date));
                    return `<span class="gantt-grid-date-cell">${startYmd}</span>`;
                },
            },
            {
                name: 'end_date',
                label: t('projects.gantt.grid.end_date', 'End'),
                align: 'center',
                width: GRID_END_DATE_COLUMN_WIDTH,
                resize: false,
                template(task: { start_date?: string; end_date?: string; duration?: number }): string {
                    const endYmd = formatGridDateYmd(resolveGridEndYmd(task));
                    return `<span class="gantt-grid-date-cell">${endYmd}</span>`;
                },
            },
        ];
        instance.config.scales = [
            {
                unit: 'month',
                step: 1,
                format: '%Y-%m',
                css: (d: Date) => buildGanttTimelineCellClasses(d, new Date(), { cellStep: 'month' }),
            },
            {
                unit: 'day',
                step: 1,
                format: '%d',
                css: (d: Date) => buildGanttTimelineCellClasses(d, new Date(), { cellStep: 'day' }),
            },
        ];

        instance.templates.task_class = (_start: Date, _end: Date, task: { id?: string; type?: 'project' | 'task'; status?: string }): string => {
            const parts: string[] = [];
            if (task.type === 'project') {
                parts.push('gantt_project');
            } else {
                parts.push(ganttStatusClassSlug(task.status ?? 'not-started'));
            }
            if (task.id !== undefined && task.id !== null && instance.hasChild(task.id)) {
                parts.push('gantt_has_child');
            }
            return parts.join(' ');
        };

        instance.templates.grid_row_class = (_start: Date, _end: Date, task: { id?: string }): string => {
            if (task.id !== undefined && task.id !== null && instance.hasChild(task.id)) {
                return 'gantt_has_child';
            }
            return '';
        };

        instance.templates.task_row_class = (_start: Date, _end: Date, task: { id?: string }): string => {
            if (task.id !== undefined && task.id !== null && instance.hasChild(task.id)) {
                return 'gantt_has_child';
            }
            return '';
        };

        instance.templates.timeline_cell_class = (_task: unknown, date: Date): string => {
            return buildGanttTimelineCellClasses(date, new Date(), { cellStep: 'day' });
        };

        instance.templates.scale_cell_class = (): string => '';
        instance.templates.task_text = (
            _start: Date,
            _end: Date,
            task: { text?: string; assigned_to?: string | null; type?: 'project' | 'task' },
        ): string => {
            const title = escapeHtmlLabel(String(task.text ?? ''));
            if (task.type === 'project') {
                return `<span class="gantt-task-inline"><span class="gantt-task-title">${title}</span></span>`;
            }

            const assignee = escapeHtmlLabel(task.assigned_to?.trim() || unassignedTaskLabel);
            return `<span class="gantt-task-inline"><span class="gantt-task-title">${title}</span><span class="gantt-task-sep" aria-hidden="true">·</span><span class="gantt-task-assignee">${assignee}</span></span>`;
        };

        instance.plugins({ marker: true });
        instance.init(container);
        instance.clearAll();
        instance.parse({ data: rows, links });
        instance.eachTask((task: { id: string }): void => {
            if (instance.hasChild(task.id)) {
                instance.open(task.id);
            }
        });
        instance.showDate(new Date());

        if (typeof instance.getMarker === 'function' && instance.getMarker(PORTFOLIO_TODAY_MARKER_ID)) {
            instance.deleteMarker(PORTFOLIO_TODAY_MARKER_ID);
        }
        const todayStart = startOfDay(new Date());
        instance.addMarker({
            id: PORTFOLIO_TODAY_MARKER_ID,
            start_date: todayStart,
            css: 'gantt_marker_portfolio_today',
            title: t('projects.gantt.today_marker_title', 'Today'),
            text: '',
        });
        if (typeof instance.renderMarkers === 'function') {
            instance.renderMarkers();
        }

        return () => {
            try {
                if (typeof instance.getMarker === 'function' && instance.getMarker(PORTFOLIO_TODAY_MARKER_ID)) {
                    instance.deleteMarker(PORTFOLIO_TODAY_MARKER_ID);
                }
                instance.clearAll();
                instance.destructor();
            } catch {
                // Ignore teardown issues when component unmounts quickly.
            }
        };
    }, [chartModel, ganttFactory, locale, projectTaskStatuses, taskColumnLabel, t, unassignedTaskLabel]);

    return (
        <Card className={cn('overflow-hidden', className)}>
            <CardHeader className="px-3 py-2">
                <div className="flex items-center justify-between gap-3">
                    <CardTitle className="flex items-center gap-2 text-[15px] leading-tight font-semibold">
                        <span className="bg-muted/50 text-muted-foreground inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium">
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: projectStatusMeta.color }} aria-hidden />
                            <span>{projectStatusMeta.label}</span>
                        </span>
                        <Link href={projectShowHref} className="hover:underline">
                            {heading}
                        </Link>
                    </CardTitle>
                    <span className="text-muted-foreground text-xs">
                        {project.tasks.length} {t('projects.index.portfolio_gantt.tasks_label', 'tasks')}
                    </span>
                </div>
            </CardHeader>
            <CardContent className="p-0">
                <style>{PROJECT_GANTT_CARD_STYLE}</style>
                <style>{portfolioThemeCss}</style>
                {ganttFactory ? (
                    <div className="portfolio-gantt-chart-viewport rounded-b-xl" style={chartHeightStyle}>
                        <div ref={containerRef} className="portfolio-gantt-card-chart" />
                    </div>
                ) : (
                    <div className="portfolio-gantt-chart-viewport rounded-b-xl" style={chartHeightStyle}>
                        <div className="portfolio-gantt-static-shell">
                            <div className="portfolio-gantt-static">
                                {staticTodayLineLeftPx !== null ? (
                                    <div className="portfolio-gantt-static-today-line" style={{ left: staticTodayLineLeftPx }} aria-hidden />
                                ) : null}
                                <div className="portfolio-gantt-static-row">
                                    <div className="portfolio-gantt-static-left-head">{taskColumnLabel}</div>
                                    <div className="grid" style={{ gridTemplateColumns: `repeat(${chartModel.days.length}, ${DAY_COLUMN_WIDTH}px)` }}>
                                        {chartModel.monthSegments.map((segment) => (
                                            <div
                                                key={segment.key}
                                                className="portfolio-gantt-static-month-cell"
                                                style={{ gridColumn: `span ${segment.span}` }}
                                            >
                                                {segment.label}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="portfolio-gantt-static-row">
                                    <div className="portfolio-gantt-static-left-head" />
                                    <div className="grid" style={{ gridTemplateColumns: `repeat(${chartModel.days.length}, ${DAY_COLUMN_WIDTH}px)` }}>
                                        {chartModel.days.map((day) => (
                                            <div
                                                key={day.key}
                                                className={cn('portfolio-gantt-static-day-cell', {
                                                    weekend: day.isWeekend,
                                                    today: day.isToday,
                                                    gantt_current_week: day.isCurrentWeek,
                                                })}
                                            >
                                                {String(day.date.getDate()).padStart(2, '0')}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {chartModel.staticRows.map((row) => {
                                    const rowStatusClass = row.isProject ? 'gantt_project' : ganttStatusClassSlug(row.status ?? 'not-started');
                                    const statusColor = resolveTaskStatusSwatchFromSettings(
                                        { type: row.isProject ? 'project' : 'task', status: row.status },
                                        projectTaskStatuses,
                                    );
                                    const secondaryText = row.isProject ? '' : row.assignedTo || unassignedTaskLabel;
                                    const secondaryTextLabel = secondaryText ? `${row.label} (${secondaryText})` : row.label;

                                    return (
                                        <div key={row.id} className="portfolio-gantt-static-row">
                                            <div
                                                className="portfolio-gantt-static-left-cell"
                                                style={{ paddingLeft: `${8 + row.depth * 12}px` }}
                                                title={secondaryTextLabel}
                                            >
                                                <div className="task-status-bubble" style={{ ['--status-color' as string]: statusColor }}>
                                                    <div className="status-indicator" />
                                                    <div className="task-content">
                                                        <div className="task-name">{row.label}</div>
                                                        {secondaryText ? <div className="text-muted">{secondaryText}</div> : null}
                                                    </div>
                                                </div>
                                            </div>
                                            <div
                                                className="portfolio-gantt-static-right-grid"
                                                style={{
                                                    gridTemplateColumns: `repeat(${chartModel.days.length}, ${DAY_COLUMN_WIDTH}px)`,
                                                }}
                                            >
                                                {chartModel.days.map((day) => (
                                                    <div
                                                        key={`${row.id}-${day.key}`}
                                                        className={cn('portfolio-gantt-static-right-cell', {
                                                            weekend: day.isWeekend,
                                                            today: day.isToday,
                                                            gantt_current_week: day.isCurrentWeek,
                                                        })}
                                                    />
                                                ))}
                                                <div
                                                    className={cn('portfolio-gantt-static-bar', rowStatusClass)}
                                                    style={{
                                                        left: `${row.startIndex * DAY_COLUMN_WIDTH + 1}px`,
                                                        width: `${Math.max(14, row.spanDays * DAY_COLUMN_WIDTH - 2)}px`,
                                                    }}
                                                    title={secondaryTextLabel}
                                                >
                                                    {row.label}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
